import { Inject, Injectable } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';

import { RoiChangeStore } from '../../stores/roi-change/roi-change.store';
import { MicroLayoutStore, RoiStore, FindingStore, SYNC_REVIEWED_FINDING_CHANGES_USE_CASE } from '../../stores';
import { SYNC_REVIEWED_ROI_CHANGES_USE_CASE, ReviewAnalysisPayload } from '../roi-analysis-review';
import { SyncReviewedRoiChangesUseCase, SyncReviewedFindingChangesUseCase } from '@analysis/use-cases';
import { SnackbarService } from '@shared/ui';

const SNACKBAR_DEBOUNCE_MS = 300;

@Injectable()
export class MicroReviewEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;
  private readonly snackbarTrigger$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly findingStore: FindingStore,
    private readonly microLayout: MicroLayoutStore,
    @Inject(SYNC_REVIEWED_ROI_CHANGES_USE_CASE)
    private readonly reviewRoiAnalysisUseCase: SyncReviewedRoiChangesUseCase,
    @Inject(SYNC_REVIEWED_FINDING_CHANGES_USE_CASE)
    private readonly reviewFindingsUseCase: SyncReviewedFindingChangesUseCase,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.snackbarTrigger$);
  }

  readonly reviewAnalysis = this.roiStore.effect((payload$: Observable<ReviewAnalysisPayload>) =>
    payload$.pipe(
      tap(() => {
        this.roiChangeStore.setSyncing(true);
        this.findingStore.setSyncingChanges(true);
        this.snackbarTrigger$.next(true);
      }),
      concatLatestFrom(() => [this.findingStore.changedFindings$, this.findingStore.newFindings$]),
      switchMap(([payload, changedFindings, newFindings]) => {
        const { sampleId, sourceAnalystId, currentAnalystId } = payload;

        return from(
          this.reviewRoiAnalysisUseCase.execute({
            sampleId,
            currentAnalystId,
            sourceAnalystId,
          })
        ).pipe(
          tap(({ updatedRois }) => {
            this.roiChangeStore.clearAllChanges();
            this.roiStore.completeSyncWithUpdatedRois(updatedRois);
            const reviewedAssetIds = Array.from(
              new Set(updatedRois.map((r) => r.assetId).filter((id): id is string => !!id))
            );
            this.microLayout.markAssetsAsAnalyzed(reviewedAssetIds);
            this.microLayout.markAssetsAsReviewed(reviewedAssetIds);
          }),
          switchMap(() =>
            from(
              this.reviewFindingsUseCase.execute({
                sampleId,
                currentAnalystId,
                sourceAnalystId,
                changedFindings,
                newFindings,
              })
            )
          ),
          tap(({ savedFindings }) => {
            this.findingStore.updateFindingsAfterSync(savedFindings);
            const reviewedFindingAssetIds = Array.from(
              new Set(savedFindings.map((f) => f.assetId).filter((id): id is string => !!id))
            );
            this.microLayout.markAssetsAsAnalyzed(reviewedFindingAssetIds);
            this.microLayout.markAssetsAsReviewed(reviewedFindingAssetIds);
          }),
          tap(() => this.snackbarService.show('analysis.review_saved')),
          map(() => undefined),
          catchError((error) => {
            this.roiChangeStore.setError(error.message);
            this.findingStore.setSyncError(error.message);
            return EMPTY;
          }),
          finalize(() => this.snackbarTrigger$.next(false))
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.saving_review', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );
}
